const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if authenticated (supports both JWT token in localStorage and httpOnly cookie)
export const isAdminAuthenticated = () => {
  // Check if we have user data stored (cookie auth will have user data but no token visible)
  return localStorage.getItem('auth_user') !== null || sessionStorage.getItem('admin_auth') !== null;
};

// Get legacy Basic auth credentials
export const getAdminAuth = () => {
  return sessionStorage.getItem('admin_auth');
};

// Get JWT token (for backward compatibility - may be null if using cookie auth)
export const getJwtToken = () => {
  return localStorage.getItem('auth_token');
};

// Get username (from either JWT user or legacy)
export const getAdminUsername = () => {
  const user = localStorage.getItem('auth_user');
  if (user) {
    try {
      const parsed = JSON.parse(user);
      return parsed.first_name || 'Admin';
    } catch {
      return 'Admin';
    }
  }
  return sessionStorage.getItem('admin_username') || 'Admin';
};

// Get current user data
export const getCurrentUser = () => {
  const user = localStorage.getItem('auth_user');
  if (!user) return null;
  try {
    return JSON.parse(user);
  } catch {
    return null;
  }
};

// Check if current user is admin
export const isAdmin = () => {
  const user = getCurrentUser();
  return user?.role === 'admin';
};

// Check if current user is editor or admin
export const isEditor = () => {
  const user = getCurrentUser();
  return user?.role === 'editor' || user?.role === 'admin';
};

// Logout - clears localStorage and calls backend to clear cookie
export const adminLogout = async () => {
  try {
    // Call backend to clear httpOnly cookie
    await fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'  // Include cookies
    });
  } catch (e) {
    // Continue with local cleanup even if backend call fails
    console.error('Logout API error:', e);
  }
  
  // Clear local storage
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  sessionStorage.removeItem('admin_auth');
  sessionStorage.removeItem('admin_username');
  window.location.href = '/admin';
};

// Fetch with authentication (supports JWT header, httpOnly cookie, and Basic auth)
export const adminFetch = async (endpoint, options = {}) => {
  const jwtToken = getJwtToken();
  const basicAuth = getAdminAuth();
  
  const headers = { ...options.headers };
  
  // Add Authorization header if we have a token (for backward compatibility)
  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  } else if (basicAuth) {
    headers['Authorization'] = `Basic ${basicAuth}`;
  }
  // If no token, the httpOnly cookie will be sent automatically with credentials: 'include'

  const response = await fetch(`${BACKEND_URL}/api${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',  // Always include cookies for httpOnly auth
  });

  if (response.status === 401) {
    // Clear local data and redirect
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    sessionStorage.removeItem('admin_auth');
    sessionStorage.removeItem('admin_username');
    window.location.href = '/admin';
    throw new Error('Session expired');
  }

  return response;
};
